import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import ifaService from '../../../services/ifaService';
import useRemoteSelectPage from '../../../hooks/useRemoteSelectPage';

const SELECT_PAGE_SIZE = 50;

const DEFAULT_LIST_PARAMS = {
  status: 'all',
  sort_by: 'created_at',
  sort_order: 'desc',
};

const getPartnerUuid = (partner) => partner?.id ?? partner?.ifa_id ?? partner?.uuid ?? '';

function getSelectOptionData(option) {
  const opt = Array.isArray(option) ? option[0] : option;
  return opt?.data ?? opt ?? {};
}

export default function PartnerRemoteSelect({
  value,
  onChange,
  size,
  className,
  allowClear = true,
  prefetchSearch = '',
  prefetchOnMount = false,
  disabled = false,
  placeholder,
  listParams,
  pageSize = SELECT_PAGE_SIZE,
  searchDebounceMs = 350,
  initialPartner = null,
}) {
  const [selectedPartnerRecord, setSelectedPartnerRecord] = useState(initialPartner);

  useEffect(() => {
    if (initialPartner && value && String(getPartnerUuid(initialPartner)) === String(value)) {
      setSelectedPartnerRecord(initialPartner);
    }
  }, [initialPartner, value]);

  const mergedListParams = listParams ?? DEFAULT_LIST_PARAMS;

  const fetchPartnersPage = useCallback(async ({ page, limit, search, signal }) => {
    const res = await ifaService.getAdminIfas({
      page,
      limit,
      search,
      ...mergedListParams,
      signal,
    });
    return {
      items: Array.isArray(res?.users) ? res.users : [],
      pagination: res?.pagination,
    };
  }, [mergedListParams]);

  const {
    items: partners,
    loading,
    handleSearch,
    handlePopupScroll,
    handleOpenChange,
    dropdownRender,
    searchValue,
    clearSearch,
  } = useRemoteSelectPage({
    fetchPage: fetchPartnersPage,
    getKey: getPartnerUuid,
    pageSize,
    searchDebounceMs,
    selectedValue: value || null,
    prefetchOnMount,
    prefetchSearch,
    onError: (e) => console.error('Failed to load partners:', e),
  });

  const partnerOptions = useMemo(() => {
    const base = partners
      .map((ifa) => {
        const id = getPartnerUuid(ifa);
        if (!id) return null;
        return {
          value: String(id),
          label: ifa.name || ifa.email || ifa.full_name || `Partner ${id}`,
          partner: ifa,
        };
      })
      .filter(Boolean);

    if (
      value &&
      selectedPartnerRecord &&
      String(getPartnerUuid(selectedPartnerRecord)) === String(value) &&
      !base.some((o) => String(o.value) === String(value))
    ) {
      const label =
        selectedPartnerRecord.name ||
        selectedPartnerRecord.email ||
        selectedPartnerRecord.full_name ||
        String(value);
      return [{ value: String(value), label, partner: selectedPartnerRecord }, ...base];
    }
    return base;
  }, [partners, value, selectedPartnerRecord]);

  const handleChange = (val, option) => {
    const data = getSelectOptionData(option);
    const nextPartner =
      data?.partner ?? partners.find((p) => String(getPartnerUuid(p)) === String(val)) ?? null;
    setSelectedPartnerRecord(val ? nextPartner : null);
    clearSearch();
    onChange(val ?? '', nextPartner);
  };

  const handleClear = () => {
    setSelectedPartnerRecord(null);
    clearSearch();
    onChange('', null);
  };

  return (
    <Select
      size={size}
      className={className}
      showSearch
      autoClearSearchValue={false}
      placeholder={placeholder ?? (loading ? 'Loading partners…' : 'Select a partner')}
      loading={loading}
      disabled={disabled}
      value={value || undefined}
      onChange={handleChange}
      onClear={handleClear}
      allowClear={allowClear}
      optionFilterProp="label"
      filterOption={false}
      searchValue={searchValue}
      onSearch={handleSearch}
      onPopupScroll={handlePopupScroll}
      onOpenChange={handleOpenChange}
      dropdownRender={dropdownRender}
      options={partnerOptions}
      labelRender={() => {
        const label =
          selectedPartnerRecord?.name ||
          selectedPartnerRecord?.email ||
          selectedPartnerRecord?.full_name ||
          partnerOptions.find((o) => String(o.value) === String(value))?.label;
        return label ? <span>{label}</span> : null;
      }}
    />
  );
}
